// src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./store";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "sonner";

// Import all your components
import LandingPage from "./components/landing/LandingPage";
import LoginPage from "./components/auth/LoginPage";
import RegisterPage from "./components/auth/RegisterPage";
// Receptionist Components
import ReceptionistDashboard from "./components/receptionist/Dashboard";
import PatientRegistrationFlow from "./components/receptionist/PatientRegistrationFlow";
import PatientSearch from "./components/receptionist/PatientSearch";
import TestOrders from "./components/receptionist/TestOrders";
import PatientDetails from "./components/receptionist/PatientDetails";
import Visits from "./components/receptionist/Visits";
import TestOrdersList from "./components/receptionist/TestOrdersList";

// Laboratory Components
import TestWorkbench from "./components/laboratory/TestWorkbench";
import ResultEntryForm from "./components/laboratory/ResultEntryForm";
import PathologistReportsList from "./components/pathologist/PathologistReportsList";
import PathologistQueue from "./components/pathologist/PathologistQueue";
import MorphologyReportEntry from "./components/pathologist/MorphologyReportEntry";

// Patient Components
import PatientDashboard from "./components/patient/Dashboard";
import PatientProfile from "./components/patient/ProfilePage";
import PatientTestHistory from "./components/patient/TestHistory";

// Admin Components
import AdminDashboard from "./components/admin/Dashboard";
import UserManagement from "./components/admin/UserManagement";
import ActivityLogs from "./components/admin/ActivityLogs";
import ClinicSettings from "./components/admin/ClinicSettings";
import TestManagement from "./components/admin/TestManagement";
import LaboratoryConfiguration from "./components/admin/LaboratoryConfiguration";
import AdminAnalytics from "./components/admin/Analytics";
import SyncCenter from "./components/admin/SyncCenter";

// Common Components
import Header from "./components/common/Header";
import Sidebar from "./components/common/Sidebar";

import { useLocation } from "react-router-dom";
import PrintReportPage from "./components/reports/printReportPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user } = useAuth();

  if (!user) {
    // console.log("no user");
    return <Navigate to="/" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

// In AppLayout:
const AppLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const location = useLocation();

  // Map paths to tab ids
  const pathToTab = (pathname) => {
    if (pathname.startsWith("/receptionist/dashboard")) return "rec-dashboard";
    if (pathname.startsWith("/receptionist/register-patient")) return "rec-register";
    if (pathname.startsWith("/receptionist/search-patient")) return "rec-search";
    if (pathname.startsWith("/receptionist/test-orders")) return "rec-orders";
    if (pathname.startsWith("/receptionist/today-visits")) return "rec-visits";
    if (pathname.startsWith("/receptionist/test-order-list")) return "rec-orders";
    if (pathname.startsWith("/receptionist/test-orders")) return "rec-orders";

    if (pathname.startsWith("/laboratory/workbench")) return "lab-workbench";
    if (pathname.startsWith("/laboratory/enter-results")) return "lab-enter-results";

    if (pathname.startsWith("/pathologist/queue")) return "pathologist-queue";
    if (pathname.startsWith("/pathologist/morphology")) return "pathologist-queue";
    if (pathname.startsWith("/reports/pathologist")) return "pathologist-reports";
    if (pathname.startsWith("/patient/dashboard")) return "patient-dashboard";
    if (pathname.startsWith("/patient/results")) return "patient-results";
    if (pathname.startsWith("/patient/profile")) return "patient-profile";
    if (pathname.startsWith("/patient/history")) return "patient-history";
    if (pathname.startsWith("/admin/dashboard")) return "admin-dashboard";
    if (pathname.startsWith("/admin/analytics")) return "admin-analytics";
    if (pathname.startsWith("/admin/users")) return "admin-users";
    if (pathname.startsWith("/admin/tests")) return "admin-tests";
    if (pathname.startsWith("/admin/clinic-settings")) return "admin-clinic";
    if (pathname.startsWith("/admin/activity-logs")) return "admin-activity";
    if (pathname.startsWith("/admin/system-settings")) return "admin-system";
    if (pathname.startsWith("/admin/sync")) return "admin-sync";
    return "";
  };

  const [activeTab, setActiveTab] = React.useState(() =>
    pathToTab(location.pathname)
  );

  React.useEffect(() => {
    setActiveTab(pathToTab(location.pathname));
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex">
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          variant="desktop"
        />
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0 overflow-x-hidden">
        <Header title={title} onToggleSidebar={() => setMobileSidebarOpen((v) => !v)} />
        <main>{children}</main>
        <footer className="border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex items-center justify-center gap-2 text-xs font-medium text-gray-600">
            <img
              src="/Bewide-logo.png"
              alt="BeWide Technologies"
              className="h-6 w-auto object-contain"
            />
            <span>developed by Bewide Technologies</span>
          </div>
        </footer>
      </div>

      {/* Mobile drawer sidebar */}
      {mobileSidebarOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/30"
            aria-label="Close sidebar"
            onClick={() => setMobileSidebarOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-80 max-w-[85vw]">
            <Sidebar
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              sidebarOpen={true}
              setSidebarOpen={() => {}}
              variant="mobile"
              onRequestClose={() => setMobileSidebarOpen(false)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Wrap your app with Redux Provider
const AppContent = () => {
  return (
    <AuthProvider>
      <div className="App">
        <Toaster 
          position="top-right"
          expand={false}
          richColors
          closeButton
          toastOptions={{
            duration: 3000,
            style: {
              background: 'white',
              border: '1px solid rgba(0,0,0,0.1)',
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            },
            className: 'sonner-toast',
          }}
        />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          {/* Receptionist Routes */}
          <Route
            path="/receptionist/dashboard"
            element={
              <ProtectedRoute allowedRoles={["receptionist", "admin"]}>
                <AppLayout title="Receptionist Dashboard">
                  <ReceptionistDashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/receptionist/register-patient"
            element={
              <ProtectedRoute allowedRoles={["receptionist", "admin"]}>
                <AppLayout title="Register Patient">
                  <PatientRegistrationFlow />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/receptionist/search-patient"
            element={
              <ProtectedRoute allowedRoles={["receptionist", "admin"]}>
                <AppLayout title="Search Patient">
                  <PatientSearch />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/receptionist/test-orders"
            element={
              <ProtectedRoute allowedRoles={["receptionist", "admin"]}>
                <AppLayout title="Test Orders">
                  <TestOrders />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/receptionist/patient-details/:id"
            element={
              <ProtectedRoute allowedRoles={["receptionist", "admin"]}>
                <AppLayout title="Patient Details">
                  <PatientDetails />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/receptionist/visits"
            element={
              <ProtectedRoute allowedRoles={["receptionist", "admin"]}>
                <AppLayout title="Visits">
                  <Visits />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/receptionist/test-order-list"
            element={
              <ProtectedRoute allowedRoles={["receptionist", "admin"]}>
                <AppLayout title="Test Orders">
                  <TestOrdersList />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AppLayout title="Analytics">
                  <AdminAnalytics />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          {/* Laboratory Routes 
          <Route
            path="/laboratory/dashboard"
            element={
              <ProtectedRoute allowedRoles={["laboratorist", "admin"]}>
                <AppLayout title="Laboratory Dashboard">
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />*/}
          {/* <Route
            path="/laboratory/pending-tests"
            element={
              <ProtectedRoute allowedRoles={["laboratorist", "admin"]}>
                <AppLayout title="Pending Tests">
                  <PendingTests />
                </AppLayout>
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="/laboratory/workbench"
            element={
              <ProtectedRoute allowedRoles={["laboratorist", "admin"]}>
                <AppLayout>
                  <TestWorkbench />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/laboratory/enter-results"
            element={
              <ProtectedRoute allowedRoles={["laboratorist", "admin"]}>
                <AppLayout title="Enter Test Results">
                  <ResultEntryForm />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/pathologist"
            element={
              <ProtectedRoute
                allowedRoles={["laboratorist", "admin", "pathologist"]}
              >
                <AppLayout title="Pathologist Reports">
                  <PathologistReportsList />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathologist/queue"
            element={
              <ProtectedRoute allowedRoles={["pathologist", "admin"]}>
                <AppLayout title="Pathologist Queue">
                  <PathologistQueue />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathologist/morphology/:orderId"
            element={
              <ProtectedRoute allowedRoles={["pathologist", "admin"]}>
                <AppLayout title="Morphology Report">
                  <MorphologyReportEntry />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          {/* Patient Routes */}
          <Route
            path="/patient/dashboard"
            element={
              <ProtectedRoute allowedRoles={["patient"]}>
                <AppLayout title="Patient Portal">
                  <PatientDashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patient/profile"
            element={
              <ProtectedRoute allowedRoles={["patient"]}>
                <AppLayout title="My Profile">
                  <PatientProfile />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patient/history"
            element={
              <ProtectedRoute allowedRoles={["patient"]}>
                <AppLayout title="Test History">
                  <PatientTestHistory />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          {/* Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AppLayout title="Admin Dashboard">
                  <AdminDashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AppLayout title="User Management">
                  <UserManagement />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tests"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AppLayout title="Test Management">
                  <TestManagement />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/system-settings"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AppLayout title="Laboratory Configuration">
                  <LaboratoryConfiguration />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/activity-logs"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AppLayout title="Activity Logs">
                  <ActivityLogs />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/sync"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AppLayout title="Sync Center">
                  <SyncCenter />
                </AppLayout>
              </ProtectedRoute>
            }
          />

        
          <Route
            path="/admin/clinic-settings"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AppLayout title="Clinic Settings">
                  <ClinicSettings />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/print-report"
            element={
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "laboratorist",
                  "receptionist",
                  "pathologist",
                  "patient",
                ]}
              >
                <PrintReportPage />
              </ProtectedRoute>
            }
          />
          {/* Fallback Routes */}
          <Route
            path="/receptionist"
            element={<Navigate to="/receptionist/dashboard" replace />}
          />
          <Route
            path="/laboratory"
            element={<Navigate to="/laboratory/workbench" replace />}
          />
          <Route
            path="/patient"
            element={<Navigate to="/patient/dashboard" replace />}
          />
          <Route
            path="/admin"
            element={<Navigate to="/admin/dashboard" replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </AuthProvider>
  );
};

// Unauthorized Page Component
const UnauthorizedPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="text-6xl mb-4">🚫</div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
        <p className="text-gray-600 mb-4">
          You don't have permission to access this page.
        </p>
        <button
          onClick={() => navigate("/")}
          className="bg-[#235F72] text-white px-6 py-2 rounded-lg hover:bg-[#1a4a5a] transition duration-200"
        >
          Go Home
        </button>
      </div>
    </div>
  );
};

// Main App Component with Redux Provider
function App() {
  return (
    <Provider store={store}>
      <Router>
        <AppContent />
      </Router>
    </Provider>
  );
}

export default App;
